<template>
  <div class="box has-background-grey-darker">
    <!-- Table Controls -->
    <div class="level mb-4">
      <!-- Page Size Selection -->
      <div class="level-left">
        <div class="level-item">
          <div class="select">
            <select v-model="pageSize">
              <option value="100">100 per page</option>
              <option value="500">500 per page</option>
              <option value="1000">1000 per page</option>
            </select>
          </div>
        </div>
        <div class="level-item">
          <span class="has-text-light ml-4">
            {{ currentBets }} of {{ totalBets }} bets
          </span>
        </div>
      </div>
      
      <!-- Pagination -->
      <div class="level-right">
        <div class="level-item">
          <nav class="pagination is-centered" role="navigation">
            <a class="pagination-previous" @click="prevPage" v-if="currentPage === 1" disabled>Previous</a>
            <a class="pagination-previous" @click="prevPage" v-else>Previous</a>
            <div class="pagination-list">
              <input 
                class="input is-small" 
                type="number" 
                v-model="currentPage" 
                :min="1" 
                :max="totalPages"
                style="width: 70px"
              >
              <span class="ml-2 has-text-light">of {{ totalPages }}</span>
            </div>
            <a class="pagination-next" @click="nextPage" v-if="currentPage === totalPages" disabled>Next</a>
            <a class="pagination-next" @click="nextPage" v-else>Next</a>
          </nav>
        </div>
      </div>
    </div>

    <!-- Bets Table -->
    <div class="table-container">
      <table class="table is-fullwidth is-striped has-background-grey-darker">
        <thead>
          <tr>
            <th v-for="header in headers" :key="header.key" @click="sort(header.key)" class="has-text-light is-clickable">
              <div class="is-flex is-align-items-center">
                {{ header.label }}
                <span class="icon is-small ml-2">
                  <i :class="{
                    'fas fa-sort': sortKey !== header.key,
                    'fas fa-sort-up': sortKey === header.key && sortDir === 'asc',
                    'fas fa-sort-down': sortKey === header.key && sortDir === 'desc'
                  }"></i>
                </span>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(bet, index) in paginatedBets" :key="`${bet.created}_${bet.sel_name}_${bet.event}_${index}`">
            <td class="has-text-light">{{ formatDate(bet.created) }}</td>
            <td class="has-text-light">{{ bet.sel_name }}</td>
            <td class="has-text-light">{{ bet.event }}</td>
            <td class="has-text-light">{{ bet.bookie_name }}</td>
            <td class="has-text-light">{{ bet.book_odds.toFixed(2) }}</td>
            <td class="has-text-light">{{ bet.bfsp ? bet.bfsp.toFixed(2) : '-' }}</td>
            <td class="has-text-light">{{ bet.win_fair_odds.toFixed(2) }}</td>
            <td class="has-text-light">{{ bet.place_fair_odds.toFixed(2)  }}</td>
            <td class="has-text-light">{{ bet.places }} at {{ bet.terms }}</td>
            <td class="has-text-light">{{ bet.ev_taken.toFixed(2) }}%</td>
            <td class="has-text-light">£{{ Number((bet.betDetails?.stake || 0)).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}</td>
            <td class="has-text-light" :class="getResultClass(bet.result)">
              {{ bet.result || 'Pending' }}
            </td>
            <td :class="{ 'has-text-success': bet.betDetails.profit > 0, 
                         'has-text-danger': bet.betDetails.profit < 0, 
                         'has-text-light': bet.betDetails.profit === 0 }">
              £{{ Number(bet.betDetails.profit).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
            </td>
            <td class="has-text-light">{{ formatPosition(bet.position) }}</td>
            <td class="has-text-light">{{ bet.metric }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue'

export default {
  name: 'BetsTable',
  props: {
    bets: {
      type: Array,
      required: true
    },
    filters: {
      type: Object,
      required: true
    },
    bankroll: {
      type: Number,
      default: 0
    },
    kellyFraction: {
      type: Number,
      default: 1
    }
  },
  setup(props) {
    const pageSize = ref(100)
    const currentPage = ref(1)
    const sortKey = ref('created')
    const sortDir = ref('asc')

    // Table headers configuration
    const headers = [
      { key: 'created', label: 'Date' },
      { key: 'sel_name', label: 'Horse' },
      { key: 'event', label: 'Event' },
      { key: 'bookie_name', label: 'Bookmaker' },
      { key: 'book_odds', label: 'Odds' },
      { key: 'bfsp', label: 'BFSP' },
      { key: 'win_fair_odds', label: 'Win Fair Odds' },
      { key: 'place_fair_odds', label: 'Place Fair Odds' },
      { key: 'place_terms', label: 'Place Terms' },
      { key: 'ev_taken', label: 'EV %' },
      { key: 'stake', label: 'Total Stake' },
      { key: 'result', label: 'Result' },
      { key: 'profit', label: 'Profit' },
      { key: 'position', label: 'Pos' },
      { key: 'metric', label: 'Metric' }
    ]

    // Sort function
    const sort = (key) => {
      if (sortKey.value === key) {
        sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc'
      } else {
        sortKey.value = key
        sortDir.value = 'asc'
      }
    }

    // Format date helper
    const formatDate = (timestamp) => {
      return new Date(parseInt(timestamp)).toLocaleString()
    }

    // Computed properties
    const sortedBets = computed(() => {
      return [...props.bets].sort((a, b) => {
        let aVal, bVal;
        
        // Handle special cases for nested properties
        if (sortKey.value === 'stake') {
          aVal = a.betDetails?.stake || 0;
          bVal = b.betDetails?.stake || 0;
        } else if (sortKey.value === 'profit') {
          aVal = a.betDetails?.profit || 0;
          bVal = b.betDetails?.profit || 0;
        } else if (sortKey.value === 'place_terms') {
          // For place terms, we need to combine places and terms
          aVal = `${a.places} at ${a.terms}`;
          bVal = `${b.places} at ${b.terms}`;
        } else {
          aVal = a[sortKey.value];
          bVal = b[sortKey.value];
        }
        
        // Handle numeric values
        if (!isNaN(parseFloat(aVal))) {
          aVal = parseFloat(aVal);
          bVal = parseFloat(bVal);
        }
        
        if (sortDir.value === 'asc') {
          return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
        } else {
          return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
        }
      })
    })

    const totalBets = computed(() => sortedBets.value?.length || 0)
    
    const currentBets = computed(() => {
      const start = (currentPage.value - 1) * pageSize.value
      const end = start + pageSize.value
      const total = sortedBets.value?.length || 0
      return Math.min(pageSize.value, total - start)
    })

    const totalPages = computed(() => {
      return Math.ceil(sortedBets.value.length / pageSize.value)
    })

    const paginatedBets = computed(() => {
      const start = (currentPage.value - 1) * pageSize.value
      const end = start + pageSize.value
      return sortedBets.value.slice(start, end)
    })

    // Navigation methods
    const nextPage = () => {
      if (currentPage.value < totalPages.value) {
        currentPage.value++
      }
    }

    const prevPage = () => {
      if (currentPage.value > 1) {
        currentPage.value--
      }
    }

    // Watch currentPage for manual input
    watch(currentPage, (newPage) => {
      // Ensure page is within bounds
      if (newPage < 1) {
        currentPage.value = 1
      } else if (newPage > totalPages.value) {
        currentPage.value = totalPages.value
      }
    })

    // Watch pageSize to reset currentPage
    watch(pageSize, () => {
      currentPage.value = 1
    })

    const formatPosition = (position) => {
      if (!position) return '-';
      
      // Map specific numbers to text
      const positionMap = {
        '254': 'PU',  // Pulled Up
        '247': 'UR',  // Unseated Rider
        '250': 'F',   // Fell
        '249': 'BD',  // Brought Down
        '99': 'NR'    // Non Runner
      };

      return positionMap[position] || position;
    }

    const getResultClass = (result) => {
      return {
        'has-text-success': result === 'Win',
        'has-text-warning': result.includes('Place'),
        'has-text-danger': result === 'Loss'
      }
    }

    return {
      pageSize,
      currentPage,
      sortKey,
      sortDir,
      headers,
      totalPages,
      paginatedBets,
      sort,
      nextPage,
      prevPage,
      formatDate,
      totalBets,
      currentBets,
      formatPosition,
      getResultClass
    }
  }
}
</script>

<style scoped>
.table {
  color: #b5b5b5;
}

.table th {
  color: #ffffff !important;
  border-color: #4a4a4a;
}

.table td {
  border-color: #4a4a4a;
}

.table.is-striped tbody tr:not(.is-selected):nth-child(2n) {
  background-color: #2f2f2f !important;
}

.table tr:hover {
  background-color: #363636 !important;
}

.is-clickable {
  cursor: pointer;
}

.pagination-previous,
.pagination-next {
  background-color: #363636;
  color: #b5b5b5;
  border-color: #4a4a4a;
}

.pagination-previous:hover,
.pagination-next:hover {
  background-color: #4a4a4a;
  color: #ffffff;
}

.pagination-previous[disabled],
.pagination-next[disabled] {
  background-color: #2b2b2b;
  color: #666666;
}

.input {
  background-color: #363636;
  color: #b5b5b5;
  border-color: #4a4a4a;
  text-align: center;
}

.select select {
  background-color: #363636;
  color: #b5b5b5;
  border-color: #4a4a4a;
}
</style>